import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { MobileBlockerGate } from '@/components/MobileBlocker';
import { clearDraft, readDraft, useDraftAutosave } from '@/hooks/useDraftAutosave';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  Connection,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useRoute, useLocation } from 'wouter';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { toUserFriendlyError } from '@/lib/error-messages';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Save,
  ArrowLeft,
  Undo2,
  Redo2,
  AlertTriangle,
  X,
  Pencil,
  ChevronRight,
  ChevronDown,
  Eye,
  Power,
  Mail,
  Clock,
  GitBranch,
  Shuffle,
  Tag,
  Trash2,
  ArrowRightLeft,
  Webhook,
  Users,
  Settings,
  Send,
  MousePointerClick,
  MailOpen,
} from 'lucide-react';
import EmailContentNode from '@/components/EmailFlowBuilder/EmailContentNode';
import TriggerNode from '@/components/EmailFlowBuilder/TriggerNode';
import ActivateFlowDialog from '@/components/EmailFlowBuilder/ActivateFlowDialog';
import ActionNode from '@/components/FlowBuilder/ActionNode';
import ConditionNode from '@/components/FlowBuilder/ConditionNode';
import DelayNode from '@/components/FlowBuilder/DelayNode';
import RandomizerNode from '@/components/FlowBuilder/RandomizerNode';
import EmailNodeConfigPanel from '@/components/EmailFlowBuilder/EmailNodeConfigPanel';
import FlowTemplateGallery from '@/components/ai/FlowTemplateGallery';
import { Sparkles } from 'lucide-react';
import { LeadCopyPanel } from '@/components/leadcopy/LeadCopyPanel';

const nodeTypes = {
  trigger: TriggerNode,
  email_content: EmailContentNode,
  action: ActionNode,
  condition: ConditionNode,
  delay: DelayNode,
  randomizer: RandomizerNode,
};

type HistorySnapshot = { nodes: Node[]; edges: Edge[] };

type EditorTab = 'editor' | 'config' | 'exit';

// ─── Action categories for the sidebar panel ─────────────────────────────────
interface ActionItem {
  type: string;
  label: string;
  icon: any;
  description: string;
  /** Dados pré-preenchidos no node ao adicionar (ex: conditionType para presets de condição). */
  defaults?: Record<string, any>;
}

interface ActionCategory {
  label: string;
  items: ActionItem[];
}

const ACTION_CATEGORIES: ActionCategory[] = [
  {
    label: 'E-mail',
    items: [
      { type: 'email_content', label: 'Enviar E-mail', icon: Send, description: 'Enviar e-mail para o lead' },
    ],
  },
  {
    label: 'Espera',
    items: [
      { type: 'delay', label: 'Aguardar', icon: Clock, description: 'Aguardar X dias/horas' },
    ],
  },
  {
    label: 'Caminho do Lead',
    items: [
      { type: 'condition', label: 'Abriu E-mail', icon: MailOpen, description: 'Bifurcar conforme abertura do e-mail anterior', defaults: { conditionType: 'opened', windowHours: 24 } },
      { type: 'condition', label: 'Clicou no Link', icon: MousePointerClick, description: 'Bifurcar conforme clique em link', defaults: { conditionType: 'clicked', windowHours: 24 } },
      { type: 'condition', label: 'Abriu N+ vezes', icon: MailOpen, description: 'Engajamento alto (múltiplas aberturas)', defaults: { conditionType: 'open_count', windowHours: 72, minCount: 2 } },
      { type: 'condition', label: 'Score ≥ N', icon: GitBranch, description: 'Bifurcar por score de engajamento', defaults: { conditionType: 'score', scoreOperator: 'gte', scoreValue: 50 } },
      { type: 'condition', label: 'Possui Tag', icon: Tag, description: 'Bifurcar conforme tag presente no contato', defaults: { conditionType: 'tag' } },
      { type: 'randomizer', label: 'Split A/B', icon: Shuffle, description: 'Dividir leads aleatoriamente', defaults: { path1Probability: 50 } },
    ],
  },
  {
    label: 'Gerenciar Lead',
    items: [
      { type: 'action', label: 'Adicionar Tag', icon: Tag, description: 'Adicionar tag ao lead', defaults: { actionType: 'add-tag' } },
      { type: 'action', label: 'Remover Tag', icon: Trash2, description: 'Remover tag do lead', defaults: { actionType: 'remove-tag' } },
      { type: 'action', label: 'Adicionar pontos ao Score', icon: GitBranch, description: 'Aumentar score do lead', defaults: { actionType: 'update_score', points: 10 } },
      { type: 'action', label: 'Inscrever em outro Fluxo', icon: ArrowRightLeft, description: 'Encadear num próximo fluxo', defaults: { actionType: 'enroll_in_flow', onAlreadyEnrolled: 'skip' } },
    ],
  },
  {
    label: 'Notificação',
    items: [
      { type: 'action', label: 'Enviar Webhook', icon: Webhook, description: 'Disparar webhook para sistema externo', defaults: { actionType: 'webhook' } },
    ],
  },
];

// ─── Validation ──────────────────────────────────────────────────────────────
function validateFlow(name: string, nodes: Node[]): string[] {
  const errors: string[] = [];

  if (!name.trim()) errors.push('O fluxo precisa de um nome.');
  if (nodes.length === 0) errors.push('Adicione pelo menos um nó ao fluxo.');

  // Trigger é obrigatório e deve estar configurado.
  const triggerNode = nodes.find((n) => n.type === 'trigger');
  if (!triggerNode) {
    errors.push('O fluxo precisa de um gatilho (trigger) — recarregue a página para auto-criar.');
  } else {
    const tType = triggerNode.data?.triggerType;
    if (!tType) {
      errors.push('Configure o tipo do gatilho do fluxo.');
    } else if (tType === 'tag_added' && !triggerNode.data?.triggerValue?.trim()) {
      errors.push('Gatilho "Tag adicionada": defina qual tag dispara o fluxo.');
    } else if (tType === 'scheduled' && !triggerNode.data?.triggerValue) {
      errors.push('Gatilho "Agendado": defina a data e hora do disparo.');
    }
  }

  // Nós executáveis só devem existir se houver pelo menos uma ligação saindo do trigger.
  const executableCount = nodes.filter((n) => n.type !== 'trigger').length;
  if (triggerNode && executableCount === 0) {
    errors.push('Adicione pelo menos uma ação após o gatilho.');
  }

  nodes.forEach((n) => {
    const label = n.data?.label || n.type;
    if (n.type === 'email_content') {
      if (!n.data?.emailSubject?.trim()) {
        errors.push(`Nó "${label}": preencha o assunto do e-mail.`);
      }
      const hasInlineHtml = !!(n.data?.emailHtml?.trim());
      const hasLibraryRef = typeof n.data?.emailId === 'number';
      if (!hasInlineHtml && !hasLibraryRef) {
        errors.push(`Nó "${label}": defina o conteúdo (rascunho inline, biblioteca ou geração por IA).`);
      }
    }
    if (n.type === 'action') {
      if (!n.data?.actionType) errors.push(`Nó "${label}": selecione um tipo de ação.`);
      if (!n.data?.value?.trim()) errors.push(`Nó "${label}": preencha o valor da ação.`);
    }
    if (n.type === 'delay') {
      const dur = Number(n.data?.duration);
      if (!dur || dur <= 0) errors.push(`Nó "${label}": duração deve ser maior que zero.`);
    }
  });

  return errors;
}

export default function FlowBuilder() {
  return (
    <MobileBlockerGate featureName="Editor de Fluxos">
      <FlowBuilderImpl />
    </MobileBlockerGate>
  );
}

function FlowBuilderImpl() {
  const [, params] = useRoute('/flows/:id');
  const [, navigate] = useLocation();
  const flowId = params?.id ? Number(params.id) : null;

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowName, setFlowName] = useState('Novo Fluxo');
  const [flowKind, setFlowKind] = useState<'automation' | 'campaign'>('automation');
  const [editingName, setEditingName] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTab>('editor');
  const [showActionsPanel, setShowActionsPanel] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Category open state for actions panel
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    ACTION_CATEGORIES.forEach(c => { initial[c.label] = true; });
    return initial;
  });

  // Delete confirmation
  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);

  // AI template gallery
  const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false);

  // Activate dialog (com seleção de público + dispatch)
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);

  // LeadCopy panel toggle (default ligado)
  const [showLeadCopy, setShowLeadCopy] = useState(true);

  // Materialização de drafts (para nodes email_content sem emailId real)
  const materializeMutation = trpc.aiEmail.materializeDraftEmails.useMutation({
    onSuccess: (data: { created: number }) => {
      if (data.created > 0) {
        toast.success(`${data.created} draft(s) de e-mail criado(s).`);
        // Recarrega o flow para atualizar nodes com emailId
        if (flowId) utils.flows.get.invalidate({ id: flowId });
        setLoaded(false);
      } else {
        toast.info('Todos os e-mails do fluxo já estão materializados.');
      }
    },
    onError: (err: { message: string }) => toast.error(toUserFriendlyError(err)),
  });

  // Undo/Redo
  const historyRef = useRef<HistorySnapshot[]>([]);
  const futureRef = useRef<HistorySnapshot[]>([]);

  const nodeIdRef = useRef(0);
  const utils = trpc.useUtils();

  // ─── Load flow ──────────────────────────────────────────────────────────────
  const { data: flow, isLoading } = trpc.flows.get.useQuery(
    { id: flowId! },
    { enabled: !!flowId }
  );

  // Chave de rascunho — estável por flow. Mesmo pra flows ainda sem id,
  // só plugamos draft após o load (flowId existe sempre no /flows/:id).
  const draftKey = useMemo(
    () => (flowId ? `flow-builder:${flowId}` : null),
    [flowId]
  );

  const updateMutation = trpc.flows.update.useMutation({
    onSuccess: () => {
      utils.flows.list.invalidate();
      setHasUnsaved(false);
      setLastSavedAt(new Date());
      if (draftKey) clearDraft(draftKey);
      toast.success('Fluxo salvo com sucesso!');
    },
    onError: (err) => {
      toast.error(toUserFriendlyError(err, 'Erro ao salvar'));
    },
  });

  // Autosave: persiste nodes/edges/name enquanto hasUnsaved=true. O flush
  // síncrono em pré-reload (forçado por update novo) preserva o trabalho
  // do usuário mesmo quando ele estiver com edição não-salva.
  useDraftAutosave({
    key: draftKey ?? "flow-builder:disabled",
    value: { nodes, edges, flowName, flowKind },
    enabled: !!draftKey && hasUnsaved,
  });

  // Restauração: ao terminar o load do flow, se existe um draft mais
  // recente que o flow.updatedAt → oferece restaurar via toast.
  useEffect(() => {
    if (!flow || !draftKey || !loaded) return;
    type DraftShape = { nodes: Node[]; edges: Edge[]; flowName: string; flowKind: 'automation' | 'campaign' };
    const draft = readDraft<DraftShape>(draftKey);
    if (!draft) return;
    // Heurística: só oferece se o draft tiver pelo menos 1 nó (além do trigger)
    // ou edges, pra não poluir a UX com restauração de "nada".
    const meaningful = (draft.nodes?.length ?? 0) > 1 || (draft.edges?.length ?? 0) > 0;
    if (!meaningful) { clearDraft(draftKey); return; }
    toast("Rascunho não salvo encontrado", {
      description: "Recuperar a última versão em edição deste fluxo?",
      duration: 15000,
      action: {
        label: "Restaurar",
        onClick: () => {
          setNodes(draft.nodes);
          setEdges(draft.edges);
          setFlowName(draft.flowName);
          setFlowKind(draft.flowKind);
          setHasUnsaved(true);
        },
      },
      cancel: { label: "Descartar", onClick: () => clearDraft(draftKey) },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, draftKey]);

  useEffect(() => {
    if (flow && !loaded) {
      setFlowName(flow.name);
      const k = (flow as { kind?: string }).kind === 'campaign' ? 'campaign' : 'automation';
      setFlowKind(k);
      const savedNodes = (flow.nodes as Node[]) ?? [];
      const savedEdges = (flow.edges as Edge[]) ?? [];

      // Garante que sempre exista UM nó trigger no canvas. Se o fluxo foi
      // criado antes da introdução do trigger node (legado) ou está vazio,
      // prepend um trigger pré-configurado a partir do triggerType do flow.
      const hasTrigger = savedNodes.some((n) => n.type === 'trigger');
      const triggerType = (flow as any).triggerType || 'manual';
      const triggerValue = (flow as any).triggerValue || '';

      let nodesToLoad = savedNodes;
      let edgesToLoad = savedEdges;

      if (!hasTrigger) {
        const triggerNode: Node = {
          id: 'node-trigger',
          type: 'trigger',
          position: { x: 80, y: 100 },
          data: {
            label: 'Gatilho do fluxo',
            triggerType,
            triggerValue,
          },
        };
        nodesToLoad = [triggerNode, ...savedNodes];
        if (savedNodes.length > 0) {
          // Liga o trigger no primeiro nó "executável" do fluxo legado.
          const firstExec = savedNodes[0];
          edgesToLoad = [
            { id: `edge-trigger-${firstExec.id}`, source: triggerNode.id, target: firstExec.id },
            ...savedEdges,
          ];
        }
      }

      setNodes(nodesToLoad);
      setEdges(edgesToLoad);

      const maxId = nodesToLoad.reduce((max, n) => {
        const num = parseInt(n.id.replace('node-', '')) || 0;
        return num > max ? num : max;
      }, 0);
      nodeIdRef.current = Math.max(maxId + 1, 1);

      setLoaded(true);
    }
  }, [flow, loaded]);

  // ─── Unsaved changes guard ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  // ─── Keyboard shortcuts ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')
      ) {
        e.preventDefault();
        redo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveFlow();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // ─── History helpers ─────────────────────────────────────────────────────────
  const pushHistory = (currentNodes: Node[], currentEdges: Edge[]) => {
    historyRef.current = [
      ...historyRef.current.slice(-49),
      { nodes: currentNodes, edges: currentEdges },
    ];
    futureRef.current = [];
  };

  const undo = () => {
    const history = historyRef.current;
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    historyRef.current = history.slice(0, -1);
    futureRef.current = [{ nodes: [...nodes], edges: [...edges] }, ...futureRef.current.slice(0, 49)];
    setNodes(prev.nodes);
    setEdges(prev.edges);
    setSelectedNode(null);
    setHasUnsaved(true);
  };

  const redo = () => {
    const future = futureRef.current;
    if (future.length === 0) return;
    const next = future[0];
    futureRef.current = future.slice(1);
    historyRef.current = [...historyRef.current.slice(-49), { nodes: [...nodes], edges: [...edges] }];
    setNodes(next.nodes);
    setEdges(next.edges);
    setSelectedNode(null);
    setHasUnsaved(true);
  };

  // ─── Operations ──────────────────────────────────────────────────────────────
  const onConnect = useCallback(
    (connection: Connection) => {
      pushHistory([...nodes], [...edges]);
      setEdges((eds) => addEdge(connection, eds));
      setHasUnsaved(true);
    },
    [nodes, edges]
  );

  const addNodeFromAction = (actionItem: ActionItem) => {
    pushHistory([...nodes], [...edges]);

    const newNode: Node = {
      id: `node-${nodeIdRef.current++}`,
      type: actionItem.type,
      position: { x: Math.random() * 400 + 200, y: Math.random() * 300 + 100 },
      data: { label: actionItem.label, ...(actionItem.defaults ?? {}) },
    };

    setNodes((nds) => [...nds, newNode]);
    setHasUnsaved(true);
    toast.success(`Nó "${actionItem.label}" adicionado ao fluxo`);
  };

  const requestDeleteNode = (nodeId: string) => {
    const target = nodes.find((n) => n.id === nodeId);
    if (target?.type === 'trigger') {
      toast.error('O gatilho ancora o fluxo e não pode ser removido. Edite o tipo no painel à direita.');
      return;
    }
    setNodeToDelete(nodeId);
  };

  const confirmDeleteNode = () => {
    if (!nodeToDelete) return;
    pushHistory([...nodes], [...edges]);
    setNodes((nds) => nds.filter((n) => n.id !== nodeToDelete));
    setEdges((eds) => eds.filter((e) => e.source !== nodeToDelete && e.target !== nodeToDelete));
    if (selectedNode?.id === nodeToDelete) setSelectedNode(null);
    setHasUnsaved(true);
    setNodeToDelete(null);
  };

  const handleNodeClick = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (node) {
      setSelectedNode(node);
      setShowActionsPanel(false);
    }
  };

  const handleUpdateNode = (nodeId: string, data: any) => {
    setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data } : n)));
    setSelectedNode((prev) => (prev ? { ...prev, data } : null));
    setHasUnsaved(true);
  };

  // ─── Save with validation ────────────────────────────────────────────────────
  const buildSavePayload = (overrides?: { status?: 'active' | 'inactive' | 'draft' }) => {
    const triggerNode = nodes.find((n) => n.type === 'trigger');
    const tType = (triggerNode?.data?.triggerType as string) ?? 'manual';
    const tValue = (triggerNode?.data?.triggerValue as string) ?? '';
    return {
      id: flowId!,
      name: flowName,
      triggerType: tType,
      triggerValue: tValue,
      nodes: nodes as any,
      edges: edges as any,
      ...(overrides ?? {}),
    };
  };

  const saveFlow = (andActivate?: boolean) => {
    if (!flowId) {
      toast.error('Nenhum fluxo selecionado para salvar.');
      return;
    }

    const errors = validateFlow(flowName, nodes);
    if (errors.length > 0) {
      errors.forEach((err, i) =>
        setTimeout(() => toast.warning(err, { icon: <AlertTriangle className="w-4 h-4" /> }), i * 120)
      );
      return;
    }

    // "Salvar e Ativar" agora abre o diálogo de ativação (com público).
    // O diálogo é responsável por chamar o updateMutation com status: 'active'
    // após o usuário confirmar audiência.
    if (andActivate) {
      // Salva primeiro (rascunho) para garantir que tudo está sincronizado
      // com flow_builder_flows antes de tentar resolver audiência/dispatch.
      updateMutation.mutate(buildSavePayload(), {
        onSuccess: () => setActivateDialogOpen(true),
      });
      return;
    }

    updateMutation.mutate(buildSavePayload());
  };

  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const handleBack = () => {
    if (hasUnsaved) {
      setConfirmLeaveOpen(true);
      return;
    }
    navigate('/flows');
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (flowId && isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const canUndo = historyRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header - Breadcrumb style */}
      <div className="border-b px-4 py-2.5 bg-card shadow-sm shrink-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8 shrink-0" aria-label="Voltar">
              <ArrowLeft className="w-4 h-4" />
            </Button>

            {/* Breadcrumb */}
            <nav className="flex items-center gap-1 text-sm min-w-0">
              <span className="text-muted-foreground hover:text-foreground cursor-pointer" onClick={handleBack}>
                E-mail
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground hover:text-foreground cursor-pointer" onClick={handleBack}>
                {flowKind === 'campaign' ? 'Campanhas' : 'Fluxos de Automação'}
              </span>
              {flowKind === 'campaign' && (
                <Badge variant="outline" className="text-[10px] ml-1 border-purple-300 bg-purple-50 text-purple-700">
                  Campanha
                </Badge>
              )}
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              {editingName ? (
                <Input
                  value={flowName}
                  onChange={(e) => { setFlowName(e.target.value); setHasUnsaved(true); }}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setEditingName(false); }}
                  className="h-7 w-48 text-sm font-medium"
                  autoFocus
                />
              ) : (
                <button
                  className="flex items-center gap-1.5 font-medium text-foreground hover:text-primary transition-colors truncate"
                  onClick={() => setEditingName(true)}
                >
                  <span className="truncate">{flowName}</span>
                  <Pencil className="w-3 h-3 shrink-0 opacity-50" />
                </button>
              )}
            </nav>

            {hasUnsaved && (
              <Badge variant="outline" className="text-xs text-yellow-700 border-yellow-300 bg-yellow-50 shrink-0 ml-2">
                Não salvo
              </Badge>
            )}
            {lastSavedAt && !hasUnsaved && (
              <span className="text-xs text-muted-foreground shrink-0 ml-2">
                Salvo às {lastSavedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Undo / Redo */}
            <Button variant="ghost" size="sm" onClick={undo} disabled={!canUndo} title="Desfazer (Ctrl+Z)" className="h-8 w-8 p-0">
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={redo} disabled={!canRedo} title="Refazer (Ctrl+Y)" className="h-8 w-8 p-0">
              <Redo2 className="w-4 h-4" />
            </Button>

            <Separator orientation="vertical" className="h-5 mx-1" />

            {/* Preview */}
            <Button variant="ghost" size="sm" disabled title="Preview" className="h-8 w-8 p-0">
              <Eye className="w-4 h-4" />
            </Button>

            <Separator orientation="vertical" className="h-5 mx-1" />

            {/* Templates (IA + Curados) — geração conversacional fica no LeadCopy */}
            <Button variant="outline" size="sm" onClick={() => setTemplateGalleryOpen(true)} className="gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
              Templates IA
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => flowId && materializeMutation.mutate({ flowId })}
              disabled={!flowId || materializeMutation.isPending}
              className="gap-1.5"
              title="Cria rascunhos reais de e-mail para nodes email_content sem emailId"
            >
              <Mail className="w-3.5 h-3.5" />
              {materializeMutation.isPending ? 'Materializando…' : 'Materializar drafts'}
            </Button>
            <Button
              variant={showLeadCopy ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setShowLeadCopy((v) => !v)}
              className="gap-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" />
              LeadCopy
            </Button>

            <Separator orientation="vertical" className="h-5 mx-1" />

            <Button
              onClick={() => saveFlow()}
              variant="outline"
              size="sm"
              disabled={updateMutation.isPending}
              className="gap-1.5"
            >
              <Save className="w-4 h-4" />
              {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button
              onClick={() => saveFlow(true)}
              size="sm"
              disabled={updateMutation.isPending}
              className="gap-1.5"
            >
              <Power className="w-4 h-4" />
              Salvar e Ativar
            </Button>
          </div>
        </div>
      </div>

      {/* Sub-header with tabs */}
      <div className="border-b bg-card px-4 shrink-0">
        <div className="flex items-center gap-0">
          {([
            { key: 'editor' as EditorTab, label: 'Editor' },
            { key: 'config' as EditorTab, label: 'Configurações' },
            { key: 'exit' as EditorTab, label: 'Saída' },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground">{nodes.length} nó{nodes.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'editor' ? (
        /* Canvas + Panels */
        <div className="flex-1 relative flex overflow-hidden">
          {/* Actions Panel (left sidebar) */}
          {showActionsPanel && !selectedNode && (
            <div className="w-[300px] bg-background border-r shadow-sm overflow-hidden shrink-0 flex flex-col">
              <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
                <h2 className="text-sm font-semibold">Adicionar Ação</h2>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowActionsPanel(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {ACTION_CATEGORIES.map((category) => (
                  <Collapsible
                    key={category.label}
                    open={openCategories[category.label]}
                    onOpenChange={() => setOpenCategories(prev => ({ ...prev, [category.label]: !prev[category.label] }))}
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-2 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50">
                      <span>{category.label}</span>
                      {openCategories[category.label] ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-1 pt-1 pb-2">
                        {category.items.map((item) => (
                          <button
                            key={item.label}
                            onClick={() => addNodeFromAction(item)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-muted/80 transition-colors group"
                          >
                            <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                              <item.icon className="w-4 h-4 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{item.label}</p>
                              <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </div>
          )}

          {/* Canvas */}
          <div className="flex-1 relative">
            {/* Floating add button */}
            {!showActionsPanel && !selectedNode && (
              <div className="absolute top-4 left-4 z-10">
                <Button
                  onClick={() => setShowActionsPanel(true)}
                  size="sm"
                  className="gap-1.5 shadow-md"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  Adicionar Ação
                </Button>
              </div>
            )}

            <ReactFlow
              nodes={nodes.map((n) => ({ ...n, selected: selectedNode?.id === n.id }))}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={(_, node) => handleNodeClick(node.id)}
              onPaneClick={() => { setSelectedNode(null); }}
              nodeTypes={nodeTypes}
              defaultViewport={{ x: 50, y: 50, zoom: 1 }}
              fitView
              fitViewOptions={{ padding: 0.3, maxZoom: 1.2, minZoom: 0.5 }}
              deleteKeyCode={null}
              // A marca "React Flow" no canto não é exigência da licença (o pacote
              // é MIT) — é um pedido dos autores, que oferecem a assinatura Pro a
              // quem a remove.
              proOptions={{ hideAttribution: true }}
            >
              <Background />
              <Controls />
              <MiniMap />
            </ReactFlow>

            {/* Empty canvas hint */}
            {nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center p-8 bg-background/80 rounded-xl border border-dashed border-border max-w-sm pointer-events-auto">
                  <div className="h-16 w-16 rounded-full bg-muted mx-auto flex items-center justify-center mb-4">
                    <GitBranch className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <p className="font-medium text-foreground mb-1">Devem percorrer o fluxo</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Adicione ações do painel lateral para construir seu fluxo de automação
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => setShowActionsPanel(true)}
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                    Selecionar uma entrada
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Config Panel (right side - when a node is selected) */}
          {selectedNode && (
            <div className="w-[420px] max-w-[40vw] bg-background border-l shadow-lg overflow-hidden shrink-0 flex flex-col">
              <div className="bg-background border-b px-4 py-3 flex items-center justify-between shrink-0">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold truncate">Configurar Nó</h2>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{selectedNode.type?.toUpperCase()} - {selectedNode.data.label || 'Sem nome'}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setSelectedNode(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <EmailNodeConfigPanel
                  node={selectedNode}
                  onUpdate={handleUpdateNode}
                  onDelete={requestDeleteNode}
                  onOpenEmailEditor={(id) => {
                    // Editor de e-mail é uma página inteira (3 painéis + LeadCopy);
                    // não cabe num drawer lateral. Abre em nova aba pra preservar o
                    // flow editor aberto e permitir alt-tab entre os dois.
                    window.open(`/emails/${id}/editor`, '_blank', 'noopener,noreferrer');
                  }}
                  onSaveNode={() => {
                    toast.success(`Nó "${selectedNode.data.label || selectedNode.type}" salvo.`);
                    setSelectedNode(null);
                  }}
                />
              </div>
            </div>
          )}

          {/* LeadCopy Panel (right side - when no node selected) */}
          {!selectedNode && showLeadCopy && flowId && (
            <div className="w-[360px] bg-background border-l shadow-sm overflow-hidden shrink-0 flex flex-col">
              <LeadCopyPanel
                mode={flowKind === 'campaign' ? 'campaign' : 'flow'}
                contextRef={{
                  kind: flowKind === 'campaign' ? 'campaign' : 'flow',
                  id: flowId,
                  name: flowName,
                  nodeCount: nodes.length,
                  nodesSummary: nodes
                    .map((n) => `- ${n.type} (${(n.data as { label?: string })?.label ?? 'sem nome'})`)
                    .join('\n')
                    .slice(0, 2500),
                }}
              />
            </div>
          )}
        </div>
      ) : activeTab === 'config' ? (
        /* Configurações Tab */
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Configurações do Fluxo</h2>
                <p className="text-sm text-muted-foreground">Configure o comportamento geral deste fluxo</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg border p-5 space-y-4">
                <h3 className="font-medium text-sm">Informações Gerais</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">Nome do Fluxo</label>
                    <Input
                      value={flowName}
                      onChange={(e) => { setFlowName(e.target.value); setHasUnsaved(true); }}
                      placeholder="Nome do fluxo"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">Status</label>
                    <Badge variant="outline" className="text-xs">
                      {flow?.status === 'active' ? 'Ativo' : flow?.status === 'inactive' ? 'Inativo' : 'Rascunho'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border p-5 space-y-4">
                <h3 className="font-medium text-sm">Gatilho de Entrada</h3>
                <p className="text-sm text-muted-foreground">
                  Defina o que inicia este fluxo. O gatilho é configurado na página de listagem de fluxos.
                </p>
                <div className="bg-muted/50 rounded-md p-3 text-sm">
                  <span className="text-muted-foreground">Gatilho atual: </span>
                  <span className="font-medium">{flow?.triggerType ?? 'keyword'}</span>
                  {flow?.triggerValue && (
                    <span className="ml-1 font-mono text-xs bg-muted px-1.5 py-0.5 rounded">"{flow.triggerValue}"</span>
                  )}
                </div>
              </div>

              <div className="rounded-lg border p-5 space-y-4">
                <h3 className="font-medium text-sm">Execução</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Permitir reentrada no fluxo</span>
                    <Badge variant="outline" className="text-xs">Em breve</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Limite de execuções simultâneas</span>
                    <Badge variant="outline" className="text-xs">Em breve</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Horário de envio</span>
                    <Badge variant="outline" className="text-xs">Em breve</Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Saída Tab */
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ArrowRightLeft className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Saída do Fluxo</h2>
                <p className="text-sm text-muted-foreground">Configure o que acontece quando o lead sai do fluxo</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg border p-5 space-y-4">
                <h3 className="font-medium text-sm">Condições de Saída</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Remover lead ao concluir fluxo</span>
                    <Badge variant="outline" className="text-xs">Em breve</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Ação ao sair (adicionar tag, mover segmento)</span>
                    <Badge variant="outline" className="text-xs">Em breve</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Notificar responsável ao concluir</span>
                    <Badge variant="outline" className="text-xs">Em breve</Badge>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-dashed p-8 text-center">
                <div className="h-12 w-12 rounded-full bg-muted mx-auto flex items-center justify-center mb-3">
                  <ArrowRightLeft className="w-6 h-6 text-muted-foreground/40" />
                </div>
                <p className="text-sm text-muted-foreground">
                  As configurações de saída estarão disponíveis em breve.
                  <br />
                  Por enquanto, os leads saem automaticamente ao final do fluxo.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm leave when unsaved */}
      <AlertDialog open={confirmLeaveOpen} onOpenChange={setConfirmLeaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair sem salvar?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações que ainda não foram salvas neste fluxo. Se sair agora, vai perdê-las.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmLeaveOpen(false); navigate('/flows'); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sair sem salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!nodeToDelete} onOpenChange={(open) => { if (!open) setNodeToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover nó?</AlertDialogTitle>
            <AlertDialogDescription>
              Este nó e todas as suas conexões serão removidos do fluxo. Você pode desfazer com Ctrl+Z.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteNode}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Template Gallery Modal */}
      <FlowTemplateGallery
        open={templateGalleryOpen}
        onOpenChange={setTemplateGalleryOpen}
        onApplyTemplate={(data) => {
          // Snapshot current state BEFORE applying so undo restores it.
          pushHistory([...nodes], [...edges]);
          setNodes(data.nodes as Node[]);
          setEdges(data.edges as Edge[]);
          if (data.name) setFlowName(data.name);
          setHasUnsaved(true);
          toast.success('Template de fluxo IA aplicado! Use "Materializar drafts" para criar os e-mails reais.');
        }}
      />

      {/* Diálogo de ativação — pede público (quando aplicável), faz preview
          de audiência e dispara executeFlowById em background. */}
      {flowId && (() => {
        const triggerNode = nodes.find((n) => n.type === 'trigger');
        const tType = (triggerNode?.data?.triggerType as string) ?? 'manual';
        const tValue = (triggerNode?.data?.triggerValue as string) ?? '';
        return (
          <ActivateFlowDialog
            open={activateDialogOpen}
            onOpenChange={setActivateDialogOpen}
            flowId={flowId}
            flowName={flowName}
            triggerType={tType}
            triggerValue={tValue}
            onActivated={async () => {
              await updateMutation.mutateAsync(buildSavePayload({ status: 'active' }));
            }}
          />
        );
      })()}

    </div>
  );
}
